import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { ProjectsList } from "./Modules";
import { TaskEditor } from "./Tasks";
import { EmptyState, PageHeader } from "../components/Ui";
import Dialog from "../components/Dialog";
import NewProjectDialog, { phaseStatusOptions } from "../components/NewProjectDialog";
import ProjectBoard, { type BoardTask } from "../components/ProjectBoard";
import { cleanTitle } from "../task-title";
import {
  ActionForm,
  DataTable,
  NoClient,
  RequestView,
  Tabs,
  label,
  useMutation,
  type RecordData,
} from "../components/Workflow";

/**
 * A client's projects: the list, the client's tasks in columns (the Tasks tab;
 * Agency > Projects > Board is projects by status, so this one is not called
 * Board), and each project's phases. One header for all three tabs; New project
 * is always there, with the client fixed. A task card opens its task in place,
 * so the page and the sidebar stay on this client.
 */
export default function ProjectsWorkspace() {
  const { clientId, client } = useClientSelection();
  const clientName = client?.name || "this client";
  const [view, setView] = useState("list");
  const [creating, setCreating] = useState(false);
  const [listRevision, setListRevision] = useState(0);
  const [picked, setPicked] = useState("");
  const [phase, setPhase] = useState<RecordData | null>(null);
  const [params, setParams] = useSearchParams();
  const openTask = params.get("task");
  const setOpenTask = (id: number | null) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (id === null) next.delete("task");
      else next.set("task", String(id));
      return next;
    });
  const list = useApi<RecordData[]>(clientId && view === "phases" ? `/api/projects?client_id=${clientId}` : null);
  const projects = (list.data || []).filter((p) => Number(p.client_id) === clientId);
  // The first project is open until another is picked.
  const selected = projects.some((p) => String(p.id) === picked) ? picked : String(projects[0]?.id ?? "");
  const project = useApi<RecordData>(view === "phases" && selected ? `/api/projects/${selected}` : null);
  // Read on every tab: an empty project list says how many of the client's tasks sit outside a project.
  const board = useApi<Record<string, BoardTask[]>>(clientId ? `/api/projects/board/${clientId}` : null);
  const mutation = useMutation();
  const phaseMutation = useMutation();
  useEffect(() => {
    setPicked("");
    setPhase(null);
  }, [clientId]);

  async function move(task: BoardTask, column: string) {
    const result = await mutation.run(`/api/projects/tasks/${task.id}/move`, { board_column: column }, "PUT");
    if (result) {
      board.reload();
      mutation.setNotice(`${cleanTitle(task.title)} moved to ${label(column)}.`);
      window.dispatchEvent(new Event("nl-aos-tasks-changed"));
    }
  }

  const newProject = (
    <button type="button" className="button" onClick={() => setCreating(true)}>
      New project
    </button>
  );
  // "No projects yet" beside a Tasks tab of 14 tasks read as a contradiction: the empty
  // state says where those tasks are, with the way there.
  const loose = Object.values(board.data || {}).flat().filter((t) => !t.project_id).length;
  const looseNote = loose > 0 ? (
    <p className="projects-loose">
      {loose === 1 ? "1 task is" : `${loose} tasks are`} not in a project.{" "}
      <button type="button" className="text-button" onClick={() => setView("tasks")}>See them in Tasks</button>
    </p>
  ) : null;
  const emptyAction = <>{newProject}{looseNote}</>;
  // Columns named for people, not fields ("Owner", not "Owner name").
  const phaseRows = ((project.data?.phases || []) as RecordData[]).map((p) => ({
    id: p.id,
    phase: p.phase_name,
    status: p.status,
    owner: p.owner_name,
    blocked: Boolean(p.is_blocked),
    tasks_done: Number(p.tasks_done || 0),
    tasks: Number(p.task_count || 0),
    source: p,
  }));

  return (
    <div className="workspace-shell">
      <PageHeader title="Projects" description="Projects, their phases and the tasks inside them." actions={clientId ? newProject : undefined} />
      {!clientId ? (
        <NoClient />
      ) : (
        <>
          <Tabs
            tabs={["list", "tasks", "phases"]}
            value={view}
            onChange={(v) => {
              setView(v);
              mutation.setNotice("");
            }}
          />
          {mutation.feedback}
          {view === "list" ? (
            <ProjectsList key={`${clientId}:${listRevision}`} clientId={clientId} clientName={clientName} newProject={emptyAction} />
          ) : view === "tasks" ? (
            <RequestView request={board}>
              <ProjectBoard
                board={board.data}
                busy={mutation.busy}
                onOpen={(task) => setOpenTask(task.id)}
                onMove={(task, column) => void move(task, column)}
              />
            </RequestView>
          ) : (
            <RequestView request={list}>
              {!projects.length ? (
                <section className="panel">
                  <EmptyState title="No projects yet" description={`Phases belong to a project. Start the first one for ${clientName}.`} action={emptyAction} />
                </section>
              ) : (
                <div className="workflow-stack">
                  <label className="select-control">
                    Project
                    <select value={selected} onChange={(e) => setPicked(e.target.value)}>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <RequestView request={project}>
                    <section className="panel">
                      <DataTable
                        rows={phaseRows}
                        columns={["phase", "status", "owner", "blocked", "tasks_done", "tasks"]}
                        empty="This project has no phases yet."
                        action={(row) => (
                          <button className="button secondary" onClick={() => setPhase(row.source)}>
                            Update phase
                          </button>
                        )}
                      />
                    </section>
                  </RequestView>
                </div>
              )}
            </RequestView>
          )}
        </>
      )}
      {creating && clientId && (
        <NewProjectDialog
          clientId={clientId}
          clientName={clientName}
          onClose={() => setCreating(false)}
          onCreated={(message) => {
            setCreating(false);
            setListRevision((n) => n + 1);
            list.reload();
            board.reload();
            mutation.setNotice(message);
          }}
        />
      )}
      {phase && (
        <Dialog title={String(phase.phase_name || "Phase")} onClose={() => setPhase(null)}>
          {phase.is_blocked && !["completed", "skipped"].includes(String(phase.status)) && (
            <p className="workflow-note">An earlier phase is not finished yet. This one can be completed once that one is done or skipped.</p>
          )}
          {/* The same choices as the agency Projects page: a phase skipped there stays Skipped here. */}
          <ActionForm
            busy={phaseMutation.busy}
            submit="Save phase"
            onCancel={() => setPhase(null)}
            fields={[
              {
                name: "status",
                options: phaseStatusOptions(phase.status),
                value: phase.status,
                hint: "Completing or skipping a phase starts the phases waiting on it.",
              },
              { name: "notes", type: "textarea", value: phase.notes },
              {
                name: "external_blocker",
                label: "Waiting on (outside the agency)",
                type: "textarea",
                value: phase.external_blocker,
              },
            ]}
            onSubmit={async (data) => {
              const result = await phaseMutation.run(`/api/projects/${selected}/phases/${phase.id}`, data, "PUT");
              if (result) {
                project.reload();
                setPhase(null);
                mutation.setNotice(`${phase.phase_name} updated.`);
              }
            }}
          />
          {phaseMutation.feedback}
        </Dialog>
      )}
      {openTask && (
        <TaskEditor
          key={openTask}
          id={openTask}
          onClose={() => setOpenTask(null)}
          onSaved={() => {
            setOpenTask(null);
            board.reload();
          }}
        />
      )}
    </div>
  );
}
